/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import {
  Button,
  Description,
  Dropdown,
  Input,
  Label,
  MultipleSelect,
  SelectButton,
  Sidebar,
} from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import {
  SLAPriority,
  SLAType,
  SLAUnitType,
  SupportHours,
  ValueRangeTypes,
} from "~/models";
import {
  useAxios,
  generateActionSuccessText,
  isSmallerThan,
  isWithinRange,
  isBiggerThan,
} from "~/tools";

type AddSLAProps = {
  id: string;
  endpoint: string;
  refetch: (id?: string) => Promise<void>;
};
type Values = {
  name: string;
  description: string;
  supportHours: string;
  type: string;
  min: number;
  max: number;
  def: number;
};

const StyledFlexContainer = styled(FlexContainer)`
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 50%;
`;

const defaultErrorMsg = "Value is not valid";

function AddSLA({ id, endpoint, refetch }: AddSLAProps) {
  const [type, setType] = React.useState<SLAType | undefined>(undefined);
  const [unitType, setUnitType] = React.useState(SLAUnitType.VALUE_RANGE);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [supportHours, setSupportHours] = React.useState("NOT_APPLICABLE");
  const [priority, setPriority] = React.useState<SLAPriority | undefined>(
    undefined
  );
  const [valueRangeType, setValueRangeType] = React.useState(
    ValueRangeTypes.HOURS
  );

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const checkValueRage = (min: number, max: number, def: number) => {
    return {
      min,
      max,
      default: def,
      unit: valueRangeType,
    };
  };
  const clean = () => {
    setType(undefined);
    setUnitType(SLAUnitType.VALUE_RANGE);
    setSupportHours("NOT_APPLICABLE");
    setValueRangeType(ValueRangeTypes.HOURS);
  };

  const onSubmit = async (values: Values) => {
    const organizeValues = {
      name: values.name,
      description: values.description,
      supportHours,
      slaType: type ? type : null,
      unitType,
      priority,
    };
    const organizeValuesRange = {
      name: values.name,
      description: values.description,
      supportHours,
      slaType: type ? type : null,
      unitType,
      priority,
      range: checkValueRage(values.min, values.max, values.def),
    };

    try {
      await Axios().post(
        `/catalog/${endpoint}/${id}/slas`,
        unitType === SLAUnitType.VALUE_RANGE
          ? organizeValuesRange
          : organizeValues
      );
      setIsSidebarOpen(false);
      showSuccess(generateActionSuccessText(`SLA`)()("added")());
      refetch();
    } catch (e) {
      setIsSidebarOpen(false);
      showError(e?.response);
    } finally {
      clean();
    }
  };
  const { register, handleSubmit, errors, watch, reset, trigger } = useForm();

  const watcher = watch();

  React.useEffect(() => {
    if (watcher.min && watcher.max) {
      trigger(["min", "max"]);
    }
    if (watcher.min && watcher.max && watcher.def) {
      trigger("def");
    }
  }, [watcher.min, watcher.max, watcher.def, trigger]);

  React.useEffect(() => {
    setPriority(undefined);
    setSupportHours("NOT_APPLICABLE");
    reset();
  }, [setPriority, setSupportHours, isSidebarOpen, reset]);

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        css={{ marginLeft: "auto" }}
        icon="plus"
      >
        Add SLA
      </Button>
      <Sidebar
        isOpen={isSidebarOpen}
        title="Add SLA"
        onClick={() => setIsSidebarOpen(false)}
      >
        <form css={{ textAlign: "left" }}>
          <Fieldset>
            <Label name="Name" required />
            <Input
              type="text"
              name="name"
              status={errors.name && "danger"}
              placeholder="Name"
              ref={register({ required: true })}
            />
            {errors.name && <Description>Name is required.</Description>}
          </Fieldset>
          <Fieldset>
            <Label name="SLA Type" />
            <Dropdown
              onChange={(value: string) => setType(value as SLAType)}
              onClear={() => setType(undefined)}
              value={type}
              options={[
                { label: "TTA", value: SLAType.TTA },
                { label: "TTR", value: SLAType.TTR },
                { label: "Range", value: SLAType.RANGE },
              ]}
              name="SLA Type"
            />
          </Fieldset>
          <Fieldset>
            <Label name="SLA Unit Type" />
            <FlexContainer>
              <MultipleSelect size="small">
                <SelectButton
                  name={SLAUnitType.VALUE_RANGE}
                  value={SLAUnitType.VALUE_RANGE}
                  labelText="Value Range"
                  onClick={() => {
                    setUnitType(SLAUnitType.VALUE_RANGE);
                  }}
                  isActive={unitType === SLAUnitType.VALUE_RANGE}
                />
                <SelectButton
                  name={SLAUnitType.BEST_EFFORT}
                  value={SLAUnitType.BEST_EFFORT}
                  labelText="Best Effort"
                  onClick={() => {
                    setUnitType(SLAUnitType.BEST_EFFORT);
                  }}
                  isActive={unitType === SLAUnitType.BEST_EFFORT}
                />
                <SelectButton
                  name={SLAUnitType.CUSTOM}
                  value={SLAUnitType.CUSTOM}
                  labelText="Custom"
                  onClick={() => {
                    setUnitType(SLAUnitType.CUSTOM);
                  }}
                  isActive={unitType === SLAUnitType.CUSTOM}
                />
              </MultipleSelect>
            </FlexContainer>
          </Fieldset>
          {unitType === SLAUnitType.VALUE_RANGE && (
            <Fieldset>
              <Label name="Value Range" />
              <FlexContainer>
                <MultipleSelect size="small">
                  <SelectButton
                    name={ValueRangeTypes.SECONDS}
                    value={ValueRangeTypes.SECONDS}
                    labelText="Seconds"
                    onClick={() => {
                      setValueRangeType(ValueRangeTypes.SECONDS);
                    }}
                    isActive={valueRangeType === ValueRangeTypes.SECONDS}
                  />
                  <SelectButton
                    name={ValueRangeTypes.MINUTES}
                    value={ValueRangeTypes.MINUTES}
                    labelText="Minutes"
                    onClick={() => {
                      setValueRangeType(ValueRangeTypes.MINUTES);
                    }}
                    isActive={valueRangeType === ValueRangeTypes.MINUTES}
                  />
                  <SelectButton
                    name={ValueRangeTypes.HOURS}
                    value={ValueRangeTypes.HOURS}
                    labelText="Hours"
                    onClick={() => {
                      setValueRangeType(ValueRangeTypes.HOURS);
                    }}
                    isActive={valueRangeType === ValueRangeTypes.HOURS}
                  />
                  <SelectButton
                    name={ValueRangeTypes.DAYS}
                    value={ValueRangeTypes.DAYS}
                    labelText="Days"
                    onClick={() => {
                      setValueRangeType(ValueRangeTypes.DAYS);
                    }}
                    isActive={valueRangeType === ValueRangeTypes.DAYS}
                  />
                  <SelectButton
                    name={ValueRangeTypes.AMOUNT}
                    value={ValueRangeTypes.AMOUNT}
                    labelText="Amount"
                    onClick={() => {
                      setValueRangeType(ValueRangeTypes.AMOUNT);
                    }}
                    isActive={valueRangeType === ValueRangeTypes.AMOUNT}
                  />
                </MultipleSelect>
              </FlexContainer>
              <FlexContainer css={{ marginTop: "1rem" }} align="flex-start">
                <StyledFlexContainer>
                  <Label name="Min:" required />
                  <Input
                    type="number"
                    name="min"
                    status={errors.min && "danger"}
                    placeholder="Min"
                    ref={register({
                      required: true,
                      validate: (val) =>
                        isSmallerThan(val, watcher.max) ||
                        `Value has to be less than ${watcher.max}`,
                    })}
                  />
                  {errors.min && (
                    <Description>
                      {errors?.min?.message || defaultErrorMsg}
                    </Description>
                  )}
                </StyledFlexContainer>
                <StyledFlexContainer css={{ marginLeft: "1rem" }}>
                  <Label name="Max:" required />
                  <Input
                    type="number"
                    name="max"
                    status={errors.max && "danger"}
                    placeholder="Max"
                    ref={register({
                      required: "Value is required.",
                      validate: (val) =>
                        isBiggerThan(val, watcher.min) ||
                        `Value has to be greater than ${watcher.min}`,
                    })}
                  />
                  {errors.max && (
                    <Description>
                      {errors?.max?.message || defaultErrorMsg}
                    </Description>
                  )}
                </StyledFlexContainer>
                <StyledFlexContainer css={{ marginLeft: "1rem" }}>
                  <Label name="Default:" required />
                  <Input
                    type="number"
                    name="def"
                    status={errors.def && "danger"}
                    placeholder="Default"
                    ref={register({
                      required: true,
                      validate: (val) =>
                        isWithinRange(val, watcher.min, watcher.max) ||
                        `Value has to be between or equal ${watcher.min} and ${watcher.max}`,
                    })}
                  />
                  {errors.def && (
                    <Description>
                      {errors?.def?.message || defaultErrorMsg}
                    </Description>
                  )}
                </StyledFlexContainer>
              </FlexContainer>
            </Fieldset>
          )}
          <Fieldset>
            <Label name="Support service hours" />
            <Dropdown
              onChange={setSupportHours}
              value={supportHours}
              options={[
                { label: "Not Applicable", value: SupportHours.NOT_APPLICABLE },
                { label: "8x5", value: SupportHours.H8D5 },
                { label: "8x7", value: SupportHours.H8D7 },
                { label: "24x5", value: SupportHours.H24D5 },
                { label: "24x7", value: SupportHours.H24D7 },
              ]}
              name="Support service hours"
            />
          </Fieldset>
          <Fieldset>
            <Label name="Priority value" />
            <Dropdown
              onClear={() => setPriority(undefined)}
              minNumOfOptionsToShowSearchBox={5}
              onChange={(value: string) => setPriority(value as SLAPriority)}
              value={priority}
              options={[
                { label: SLAPriority.P1, value: SLAPriority.P1 },
                { label: SLAPriority.P2, value: SLAPriority.P2 },
                { label: SLAPriority.P3, value: SLAPriority.P3 },
                { label: SLAPriority.P4, value: SLAPriority.P4 },
              ]}
              name="Priority value"
            />
          </Fieldset>
          <Fieldset>
            <Label name="Info" />
            <Input
              type="text"
              name="description"
              placeholder="Info"
              ref={register}
            />
          </Fieldset>
          <FlexContainer>
            <Button
              disabled={
                !(watcher.name && watcher.min && watcher.max && watcher.def)
              }
              onClick={handleSubmit(onSubmit)}
            >
              Add New SLA
            </Button>
            <Button
              severity="medium"
              type="button"
              css={{ marginLeft: "auto" }}
              onClick={() => {
                setIsSidebarOpen(false);
              }}
            >
              Cancel
            </Button>
          </FlexContainer>
        </form>
      </Sidebar>
    </>
  );
}

export { AddSLA };
